/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { NotificationService } from '@alfresco/adf-core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AssistantProvider, AssistantService, AssistantSettings, ProviderType, TestResult } from './assistant.service';

interface ProviderKind {
  id: ProviderType;
  label: string;
  url: string;
  /** Whether the service needs an API key. */
  key: boolean;
  /** Fixed for the named services; an OpenAI-format endpoint can be either. */
  location: 'local' | 'cloud' | 'either';
}

/**
 * Settings › ผู้ช่วย AI: whether the assistant is on, which AI services it may
 * use, and the rules it follows. For administrators only — the sidecar refuses
 * anyone else, and the settings page does not offer the category to them.
 *
 * Every change is saved as it is made. The switch in particular should do what
 * it says at once: turning it off takes the chat button away from everyone.
 */
@Component({
  selector: 'app-assistant-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './assistant-settings.component.html',
  styleUrls: ['./assistant-settings.component.scss']
})
export class AssistantSettingsComponent implements OnInit {
  private readonly assistant = inject(AssistantService);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);

  readonly kinds: ProviderKind[] = [
    { id: 'ollama', label: 'Ollama', url: 'http://localhost:11434', key: false, location: 'local' },
    { id: 'openai', label: 'OpenAI', url: 'https://api.openai.com/v1', key: true, location: 'cloud' },
    { id: 'anthropic', label: 'Anthropic Claude', url: 'https://api.anthropic.com', key: true, location: 'cloud' },
    { id: 'gemini', label: 'Google Gemini', url: 'https://generativelanguage.googleapis.com/v1beta/openai', key: true, location: 'cloud' },
    { id: 'azure', label: 'Azure OpenAI', url: 'https://YOUR-RESOURCE.openai.azure.com', key: true, location: 'cloud' },
    { id: 'compat', label: 'OpenAI-compatible (vLLM / LM Studio)', url: 'http://localhost:8000/v1', key: false, location: 'either' }
  ];

  readonly levels = [
    { id: 'public', label: 'DMS.ASSISTANT.LEVEL_PUBLIC' },
    { id: 'internal', label: 'DMS.ASSISTANT.LEVEL_INTERNAL' },
    { id: 'confidential', label: 'DMS.ASSISTANT.LEVEL_CONFIDENTIAL' },
    { id: 'secret', label: 'DMS.ASSISTANT.LEVEL_SECRET' }
  ];

  readonly settings = signal<AssistantSettings | null>(null);
  readonly failed = signal<string | null>(null);
  readonly saving = signal(false);

  /** A copy of the provider being edited; changes reach the list only on save. */
  readonly draft = signal<AssistantProvider | null>(null);
  private draftIndex = -1;
  keyDraft = '';
  readonly models = signal<string[]>([]);
  readonly modelsBusy = signal(false);
  readonly modelsError = signal<string | null>(null);
  readonly result = signal<TestResult | null>(null);
  readonly testing = signal(false);
  readonly confirmRemove = signal<string | null>(null);

  ngOnInit(): void {
    this.assistant.settings().subscribe({
      next: (settings) => this.settings.set(settings),
      error: (error) => this.failed.set(error?.status === 403 ? 'DMS.ASSISTANT.ADMIN_ONLY' : 'DMS.ASSISTANT.UNREACHABLE')
    });
  }

  // ── the switch and the rules ────────────────────────────────────────────

  setEnabled(enabled: boolean): void {
    this.persist({ enabled });
  }

  setAutoClassify(autoClassify: boolean): void {
    this.persist({ autoClassify });
  }

  setDocInsights(docInsights: boolean): void {
    this.persist({ docInsights });
  }

  setFormDrafts(formDrafts: boolean): void {
    this.persist({ formDrafts });
  }

  setDefault(provider: AssistantProvider): void {
    this.persist({ defaultProviderId: provider.id ?? null });
  }

  toggleLevel(level: string, allowed: boolean): void {
    const current = this.settings()?.cloudAllowedLevels ?? [];
    this.persist({ cloudAllowedLevels: allowed ? [...current, level] : current.filter((l) => l !== level) });
  }

  setNumber(field: 'maxPagesPerQuestion' | 'historyDays', value: number | string): void {
    const number = Number(value);
    if (Number.isFinite(number)) {
      this.persist({ [field]: number });
    }
  }

  /** Saves the settings with some fields changed. Stored keys are left as they are. */
  private persist(changes: Partial<AssistantSettings>, providers?: AssistantProvider[], done?: () => void): void {
    const current = this.settings();
    if (!current) {
      return;
    }

    const next: AssistantSettings = {
      ...current,
      ...changes,
      providers: (providers ?? current.providers).map((p) => ({ ...p, apiKey: p.apiKey === undefined ? null : p.apiKey }))
    };

    this.saving.set(true);
    this.assistant.saveSettings(next).subscribe({
      next: (saved) => {
        this.saving.set(false);
        this.settings.set(saved);
        this.notification.showInfo(this.translate.instant('DMS.ASSISTANT.SAVED'));
        done?.();
      },
      error: (error) => {
        this.saving.set(false);
        this.notification.showError(this.explain(error, 'DMS.ASSISTANT.SAVE_FAILED'));
      }
    });
  }

  // ── providers ───────────────────────────────────────────────────────────

  kindOf(type: ProviderType): ProviderKind {
    return this.kinds.find((k) => k.id === type) ?? this.kinds[0];
  }

  add(): void {
    this.draftIndex = -1;
    this.openDraft({ type: 'ollama', name: '', url: this.kinds[0].url, model: '', location: 'local', status: 'none' });
  }

  edit(provider: AssistantProvider, index: number): void {
    this.draftIndex = index;
    this.openDraft({ ...provider });
  }

  private openDraft(provider: AssistantProvider): void {
    this.draft.set(provider);
    this.keyDraft = '';
    this.result.set(null);
    this.modelsError.set(null);
    this.models.set(provider.model ? [provider.model] : []);
    this.confirmRemove.set(null);
    this.loadModels();
  }

  changeType(type: ProviderType): void {
    const kind = this.kindOf(type);
    this.draft.update((d) => d && { ...d, type, url: kind.url, model: '', location: kind.location === 'cloud' ? 'cloud' : 'local' });
    this.models.set([]);
    this.result.set(null);
  }

  patchDraft(changes: Partial<AssistantProvider>): void {
    this.draft.update((d) => d && { ...d, ...changes });
    this.result.set(null);
  }

  /** The draft as the server needs it to try the service: with the key typed, if one was. */
  private attempt(): AssistantProvider {
    const draft = this.draft() as AssistantProvider;
    return { ...draft, apiKey: this.keyDraft || null };
  }

  loadModels(): void {
    const draft = this.draft();
    if (!draft?.url || (this.kindOf(draft.type).key && !this.keyDraft && !draft.keyMask)) {
      return;
    }

    this.modelsBusy.set(true);
    this.modelsError.set(null);
    this.assistant.models(this.attempt()).subscribe({
      next: ({ models }) => {
        this.modelsBusy.set(false);
        const current = this.draft()?.model;
        this.models.set(current && !models.includes(current) ? [current, ...models] : models);
      },
      error: (error) => {
        this.modelsBusy.set(false);
        this.modelsError.set(this.explain(error, 'DMS.ASSISTANT.MODELS_FAILED'));
      }
    });
  }

  runTest(): void {
    if (!this.draft()) {
      return;
    }
    this.testing.set(true);
    this.result.set(null);
    this.assistant.test(this.attempt()).subscribe({
      next: (result) => {
        this.testing.set(false);
        this.result.set(result);
      },
      error: (error) => {
        this.testing.set(false);
        this.result.set({
          status: 'fail',
          steps: [
            {
              title: this.translate.instant('DMS.ASSISTANT.TEST'),
              ok: false,
              warn: false,
              detail: error?.error?.code === 'key-required' ? this.explain(error, '') : (error?.error?.message ?? String(error?.status)),
              ms: 0
            }
          ]
        });
      }
    });
  }

  saveDraft(): void {
    const draft = this.draft();
    const current = this.settings();
    if (!draft || !current) {
      return;
    }

    const saved: AssistantProvider = {
      ...draft,
      name: draft.name.trim() || this.kindOf(draft.type).label,
      apiKey: this.keyDraft ? this.keyDraft : null,
      status: this.result()?.status ?? draft.status
    };
    const providers = [...current.providers];
    if (this.draftIndex >= 0) {
      providers[this.draftIndex] = saved;
    } else {
      providers.push(saved);
    }

    // the first provider there is becomes the default
    const defaultProviderId = current.defaultProviderId ?? saved.id ?? null;
    this.persist({ defaultProviderId }, providers, () => this.closeDraft());
  }

  closeDraft(): void {
    this.draft.set(null);
    this.draftIndex = -1;
    this.keyDraft = '';
  }

  remove(provider: AssistantProvider): void {
    if (this.confirmRemove() !== provider.id) {
      this.confirmRemove.set(provider.id ?? null);
      return;
    }
    const current = this.settings();
    if (current) {
      this.confirmRemove.set(null);
      this.persist(
        {},
        current.providers.filter((p) => p.id !== provider.id)
      );
    }
  }

  statusLabel(status: string): string {
    return 'DMS.ASSISTANT.STATUS_' + status.toUpperCase();
  }

  initials(provider: AssistantProvider): string {
    return { ollama: 'Ol', openai: 'AI', anthropic: 'Cl', gemini: 'Ge', azure: 'Az', compat: 'vL' }[provider.type] ?? '?';
  }

  /** The service's own message, or — when a changed address needs the key again — that, in the reader's language. */
  private explain(error: any, fallbackKey: string): string {
    if (error?.error?.code === 'key-required') {
      return this.translate.instant('DMS.ASSISTANT.KEY_REQUIRED');
    }
    return error?.error?.message ?? this.translate.instant(fallbackKey);
  }
}
